import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Printer, Pencil, Save, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Ligne({ label, largeur = "flex-1", testid }) {
  return (
    <div className={`${largeur} flex items-end gap-2`} data-testid={testid}>
      <span className="text-[9px] uppercase tracking-wide text-gray-500 whitespace-nowrap pb-0.5">{label}</span>
      <span className="flex-1 border-b border-dotted border-gray-400 h-5" />
    </div>
  );
}

function Case({ label, testid }) {
  return (
    <label className="flex items-center gap-2" data-testid={testid}>
      <span className="inline-block h-4 w-4 border border-gray-600" />
      <span className="text-[11px] text-gray-800">{label}</span>
    </label>
  );
}

function SectionTitre({ children }) {
  return (
    <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-white bg-gray-900 px-3 py-1.5 mt-5 mb-3">{children}</h3>
  );
}

export default function AdminPaperForm({ readOnly = false }) {
  const [doc, setDoc] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/formulaire-papier`, { withCredentials: true }).then((r) => setDoc(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/formulaire-papier`, form, { withCredentials: true });
      setDoc(data);
      setEdit(false);
      toast.success("Formulaire mis à jour");
    } catch {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (!doc) return <p className="text-[#71717A] text-sm">Chargement…</p>;

  return (
    <div data-testid="admin-paper-form">
      <style>{`
        @media print {
          html, body { background: #ffffff !important; }
          body * { visibility: hidden; }
          #paper-form-doc, #paper-form-doc * { visibility: visible; }
          #paper-form-doc { position: absolute; left: 0; top: 0; width: 100%; margin: 0; box-shadow: none; min-height: auto !important; height: auto !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>

      {/* Barre d'outils — non imprimée */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8" data-testid="paper-form-toolbar">
        <div>
          <h2 className="font-display text-xl uppercase font-bold">Formulaire d'inscription papier</h2>
          <p className="text-xs text-[#71717A] mt-1">À imprimer pour les inscriptions au bureau. {readOnly ? "Seul l'administrateur principal peut modifier son contenu." : ""}</p>
        </div>
        <div className="flex gap-3">
          {!readOnly && !edit && (
            <button onClick={() => { setForm({ titre: doc.titre, sous_titre: doc.sous_titre, infos_ligne: doc.infos_ligne, intro: doc.intro, conditions: doc.conditions, note_pied: doc.note_pied }); setEdit(true); }}
              data-testid="paper-form-edit-button"
              className="flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-black transition-colors">
              <Pencil size={14} /> Modifier le contenu
            </button>
          )}
          <button onClick={() => window.print()} data-testid="paper-form-print-button"
            className="flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors">
            <Printer size={14} /> Imprimer
          </button>
        </div>
      </div>

      {/* Panneau d'édition — admin uniquement */}
      {edit && form && (
        <div className="border border-[#C5A059]/40 bg-[#121214] p-8 mb-8" data-testid="paper-form-editor">
          <h3 className="font-display uppercase font-bold mb-6">Modifier le contenu du formulaire</h3>
          <div className="space-y-4">
            <input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Titre du formulaire" data-testid="paper-edit-titre"
              className="w-full bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input value={form.sous_titre} onChange={(e) => setForm({ ...form, sous_titre: e.target.value })} placeholder="Sous-titre" data-testid="paper-edit-sous-titre"
              className="w-full bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input value={form.infos_ligne} onChange={(e) => setForm({ ...form, infos_ligne: e.target.value })} placeholder="Ligne d'infos (détection, frais…)" data-testid="paper-edit-infos"
              className="w-full bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} placeholder="Texte d'introduction" rows={2} data-testid="paper-edit-intro"
              className="w-full bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <textarea value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} placeholder="Conditions de participation" rows={8} data-testid="paper-edit-conditions"
              className="w-full bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input value={form.note_pied} onChange={(e) => setForm({ ...form, note_pied: e.target.value })} placeholder="Note de pied de page" data-testid="paper-edit-note"
              className="w-full bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={save} disabled={saving} data-testid="paper-form-save-button"
              className="flex items-center gap-2 bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-50">
              <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button onClick={() => setEdit(false)} data-testid="paper-form-cancel-button"
              className="flex items-center gap-2 border border-[#27272A] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#C5A059] transition-colors">
              <X size={14} /> Annuler
            </button>
          </div>
        </div>
      )}

      {/* ===== Document A4 imprimable ===== */}
      <div className="overflow-x-auto pb-4">
        <div id="paper-form-doc" data-testid="paper-form-doc"
          className="bg-white text-gray-900 mx-auto shadow-2xl w-[794px] min-h-[1123px] p-10 shrink-0">
          {/* En-tête */}
          <div className="flex items-center justify-between border-b-2 border-gray-900 pb-4">
            <div className="flex items-center gap-4">
              <img src="/assets/logo-light.jpg" alt="Logo D2R" className="h-16 w-auto" />
              <div>
                <p className="text-lg font-black uppercase tracking-wide leading-tight">Dream2Reality</p>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500">Football Agency</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Réf. (réservé à l'agence)</p>
              <p className="font-mono text-sm border border-gray-400 px-3 py-1 mt-1">D2R-________</p>
            </div>
          </div>

          <div className="text-center mt-6">
            <h1 className="text-xl font-black uppercase tracking-wide" data-testid="paper-titre">{doc.titre}</h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">{doc.sous_titre}</p>
            <p className="text-xs font-semibold mt-2 border border-gray-900 inline-block px-4 py-1">{doc.infos_ligne}</p>
          </div>

          <p className="text-[10px] text-gray-600 italic text-center mt-3">{doc.intro}</p>

          {/* Informations du joueur */}
          <SectionTitre>Informations du joueur</SectionTitre>
          <div className="flex gap-6">
            <div className="flex-1">
              <div className="flex gap-6 mb-3">
                <Ligne label="Prénom *" testid="paper-f-prenom" />
                <Ligne label="Nom *" testid="paper-f-nom" />
              </div>
              <div className="flex gap-6 mb-3">
                <Ligne label="Date de naissance *" testid="paper-f-naissance" />
                <Ligne label="Téléphone *" testid="paper-f-telephone" />
              </div>
              <Ligne label="Email *" testid="paper-f-email" />
            </div>
            <div className="w-[132px] shrink-0">
              <div className="border border-gray-500 h-[170px] flex items-center justify-center bg-gray-50" data-testid="paper-photo-box">
                <span className="text-[8px] uppercase tracking-widest text-gray-400 text-center leading-relaxed px-2">Photo<br />d'identité<br />35 × 45 mm</span>
              </div>
            </div>
          </div>
          <div className="flex gap-6 my-3">
            <Ligne label="Club actuel" testid="paper-f-club" />
          </div>
          <Ligne label="Lien vidéo highlight (YouTube, etc.)" testid="paper-f-video" />

          {/* Profil sportif */}
          <SectionTitre>Profil sportif</SectionTitre>
          <div className="flex gap-6 mb-3">
            <Ligne label="Poste principal *" testid="paper-f-poste" />
            <Ligne label="Poste secondaire" testid="paper-f-poste2" />
          </div>
          <div className="flex gap-6 mb-3">
            <Ligne label="Taille (cm)" largeur="w-1/3" testid="paper-f-taille" />
            <Ligne label="Poids (kg)" largeur="w-1/3" testid="paper-f-poids" />
          </div>
          <div className="flex items-center gap-5 mt-2" data-testid="paper-f-maillot">
            <span className="text-[9px] uppercase tracking-wide text-gray-500">Taille du maillot * :</span>
            {["S", "M", "L", "XL", "XXL"].map((t) => <Case key={t} label={t} testid={`paper-maillot-${t}`} />)}
          </div>

          {/* Paiement */}
          <SectionTitre>Mode de paiement des frais (20 $US)</SectionTitre>
          <div className="flex gap-8" data-testid="paper-f-paiement">
            <Case label="Carte bancaire (Stripe)" testid="paper-pay-carte" />
            <Case label="Mobile Money (Orange, M-Pesa, Airtel, Illico)" testid="paper-pay-mm" />
            <Case label="Espèces au bureau" testid="paper-pay-bureau" />
          </div>

          {/* Conditions */}
          <SectionTitre>Conditions de participation</SectionTitre>
          <p className="text-[9px] text-gray-600 leading-relaxed whitespace-pre-line" data-testid="paper-conditions">{doc.conditions}</p>

          {/* Signatures */}
          <div className="flex gap-10 mt-6">
            <div className="flex-1">
              <p className="text-[9px] uppercase tracking-wide text-gray-500 mb-1">Fait à Kinshasa, le</p>
              <span className="block border-b border-dotted border-gray-400 h-5 w-2/3" />
            </div>
            <div className="flex-1">
              <p className="text-[9px] uppercase tracking-wide text-gray-500 mb-1">Signature du joueur (ou parent / tuteur si mineur) *</p>
              <span className="block border-b border-dotted border-gray-400 h-8" />
            </div>
          </div>

          {/* Réservé à l'agence */}
          <div className="border-2 border-gray-900 mt-6 p-4" data-testid="paper-agence">
            <p className="text-[9px] uppercase tracking-[0.2em] font-bold mb-3">Réservé à l'agence D2R</p>
            <div className="flex gap-6 mb-3">
              <Ligne label="Reçu par" testid="paper-a-recu" />
              <Ligne label="Équipe assignée" testid="paper-a-equipe" />
            </div>
            <div className="flex gap-8">
              <Case label="Paiement reçu et validé" testid="paper-a-paye" />
              <Case label="Email de confirmation envoyé" testid="paper-a-email" />
              <Case label="Saisi dans le système" testid="paper-a-saisi" />
            </div>
          </div>

          {/* Pied de page */}
          <p className="text-[9px] text-gray-500 text-center mt-6 pt-3 border-t border-gray-300" data-testid="paper-note">{doc.note_pied}</p>
        </div>
      </div>
    </div>
  );
}
